/**
 * ModalHandler.js - Main Modal Orchestrator (Minimal Error Integration)
 * -------------------------------------------------------
 * Serves as the high-level coordinator for modal lifecycle operations in the contact system.
 * Delegates actual logic to lower-level modules with essential error tracking.
 */

import ModalOperations from './ModalOperations.js';
import ModalValidator from '../utils/ModalValidator.js';
import HydrationManager from '../utils/HydrationManager.js';
import EventEmitter from '../utils/EventEmitter.js';
import DebugHelper from '../utils/DebugHelper.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

export default class ModalHandler {
    constructor(namespace) {
        this.namespace = namespace;
        this.logger = DebugHelper.createContext('ModalHandler');
        
        // Minimal error handler setup
        this.errorHandler = new ErrorHandler({
            module: 'ModalHandler',
            maxErrors: 20
        });
        
        this._initialize();
    }

    _initialize() {
        try {
            if (!this.namespace) {
                throw new Error('Namespace is required');
            }

            // Initialize managers
            this.hydrationManager = new HydrationManager();
            this.validator = new ModalValidator(this.namespace);
            this.operations = new ModalOperations(this.namespace, this.hydrationManager);
            this.eventEmitter = new EventEmitter();
            
            // Setup event forwarding
            this.operations.on('modal-opened', (data) => this.eventEmitter.emit('modal-opened', data));
            this.operations.on('modal-failed', (data) => {
                this.errorHandler.handleError('Modal operation failed', new Error(data?.reason), {
                    category: 'modal-operation',
                    reason: data?.reason
                });
                this.eventEmitter.emit('modal-failed', data);
            });
            
            // Initialize hydration
            this.hydrationManager.initialize();
            
        } catch (error) {
            this.errorHandler.handleFatalError('ModalHandler initialization failed', error, {
                category: 'initialization'
            });
            throw error;
        }
    }

    // Core operations with minimal error tracking
    async openModal() {
        try {
            const result = await this.operations.openModal();
            if (!result) {
                this.errorHandler.handleError('Modal open returned false', new Error('Open failed'), {
                    category: 'modal-open'
                });
            }
            return result;
        } catch (error) {
            this.errorHandler.handleError('Modal open threw exception', error, {
                category: 'modal-open'
            });
            return false;
        }
    }

    async performHealthCheck() {
        try {
            return await this.operations.performHealthCheck();
        } catch (error) {
            this.errorHandler.handleError('Health check failed', error, {
                category: 'health-check'
            });
            return { healthy: false, issues: ['Health check exception'] };
        }
    }

    async isModalReady() {
        try {
            return await this.operations.isModalReady();
        } catch (error) {
            this.errorHandler.handleError('Readiness check failed', error, {
                category: 'readiness-check'
            });
            return false;
        }
    }

    async getModalStatus() {
        try {
            return await this.operations.getModalStatus();
        } catch (error) {
            this.errorHandler.handleError('Status check failed', error, {
                category: 'status-check'
            });
            return { status: 'error', error: error.message };
        }
    }

    async getTroubleshootingInfo() {
        try {
            const result = await this.operations.getTroubleshootingInfo();
            // Add basic error stats
            result.errorStats = this.errorHandler.getErrorStats();
            return result;
        } catch (error) {
            this.errorHandler.handleError('Troubleshooting info failed', error, {
                category: 'troubleshooting'
            });
            return { error: error.message, errorStats: this.errorHandler.getErrorStats() };
        }
    }

    // Utility methods (unchanged)
    isServerSide() {
        return this.hydrationManager?.isServerSide() || true;
    }

    isReady() {
        return this.hydrationManager?.isReady() || false;
    }

    // Event handling (unchanged)
    on(eventType, listener) {
        this.eventEmitter.on(eventType, listener);
    }

    off(eventType, listener) {
        this.eventEmitter.off(eventType, listener);
    }

    // Minimal error access methods
    getRecentErrors(minutes = 5) {
        return this.errorHandler.getRecentErrors(minutes);
    }

    hasCriticalIssues() {
        return this.errorHandler.hasFatalErrors();
    }

    destroy() {
        try {
            this.operations?.destroy();
            this.hydrationManager?.destroy();
            this.eventEmitter?.destroy();
        } catch (error) {
            this.errorHandler.handleError('Destruction failed', error, {
                category: 'cleanup'
            });
        }
    }
}